import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import PageTitle from "@components/Common/PageTitle";
import MessageInfo from "@components/Common/MessageInfo";
import {
    customColumnProps,
    DynamicTable,
} from "@maherunlocker/custom-react-table";
import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from "react";
import { useTranslation } from "react-i18next/";
import { useSnapshot } from "valtio";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { store as useStore } from "@components/VerticalLayout/store";
import { getPathAuthorization } from "@components/VerticalLayout";
import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { useAuthHeader } from "../../../../api/headersKeycloak";
import BadgeNumber from "@components/Common/BadgeNumber";

import {
    setModify,
    setNumberActive,
    setNumberArchived,
    setSelectedApplicationSco,
    store,
    setIsEdited,
    setIsActionsClicked,
    setActions,
    setValidationAction,
} from "../store";
import { deviceStore } from "./store";
import { useMergedAppsForMoneticStore } from "../../PeripheralConfigure/MoneticPeripheralConfigure/useMergedAppsForMoneticStore";
import type { PeripheralsInUseMap } from "../../PeripheralConfigure/MoneticPeripheralConfigure/useMergedAppsForMoneticStore";

import { CustomSideFilter } from "./CustomSideFilter";
import { AddApplication } from "./AddApplication";
import { CustomIcons } from "./CustomIcons";
import ModalsContainer from "./ModalsContainer";
import { APP_ID, APPLICATIONS_URL, PRINTER_HISTORY_URL } from "./constants";

type GlobalReduxState = {
    global: {
        selectedAccount: string;
        infoUser: { profileAuthorization: unknown[] };
    };
};

type DataResponse = {
    data?: Record<string, unknown>[];
    othersData?: {
        activedApplications?: number;
        archivedApplications?: number;
        peripheralsInUse?: PeripheralsInUseMap;
    };
};

type SelectedRow = {
    appId?: string;
    shopId?: string;
    peripheralId?: string;
    peripheralType?: string;
    [key: string]: unknown;
};

type TableCellContext = {
    selectedRow: SelectedRow & { subRows?: SelectedRow[] };
};

const INITIAL_MODALS = {
    addApplication: false,
    history: false,
    confirmArchived: false,
    confirmActived: false,
    deviceAssociate: false,
    deviceDissociate: false,
    multiDissociate: false,
    initialization: false,
    printerOriginHistory: false,
    moneticHistory: false,
    cashManagementHistory: false,
};

export default function PosDeviceSetup() {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const location = useLocation();

    // Redux selectors
    const selectedAccount = useSelector(
        (state: GlobalReduxState) => state.global.selectedAccount
    );
    const authorizationList = useSelector(
        (state: GlobalReduxState) => state.global.infoUser.profileAuthorization
    );

    // Valtio stores
    const { oneShop, userID, franchiseID, shopID } = useSnapshot(useStore);
    const {
        isEdited,
        actions,
        validationAction,
        isActionsClicked,
    } = useSnapshot(store);
    const {
        isConfigurationPrinterClicked,
        isConfigurationCashManagementClicked,
        isConfigurationMoneticClicked,
    } = useSnapshot(deviceStore);

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUserRef = useRef(localStorage.getItem("uuidUser"));
    const didMountRef = useRef(false);

    // State
    const [data, setData] = useState<DataResponse>({});
    const [selectedRows, setSelectedRows] = useState<SelectedRow[]>([]);
    const [editedData, setEditedData] = useState<Record<string, unknown>>({});
    const [typeDisplay, setTypeDisplay] = useState("notArchived");
    const [isDataUpdated, setIsDataUpdated] = useState<boolean | number>(false);
    const [filterActive, setLocalFilterActive] = useState(false);
    const [urlHistoryPrinter, setUrlHistoryPrinter] = useState("");
    const [modals, setModals] = useState(INITIAL_MODALS);
    const [
        isAddNewApplicationButtonClicked,
        setIsAddNewApplicationButtonClicked,
    ] = useState(false);
    const [isApplicationArchived, setIsApplicationArchived] = useState(false);

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );

    const toggleModal = useCallback(
        (key: keyof typeof INITIAL_MODALS, value: boolean) => {
            setModals((prev) => {
                if (prev[key] === value) return prev;
                return { ...prev, [key]: value };
            });
        },
        []
    );

    const authHeaderRef = useRef(getAuthHeader);
    useEffect(() => {
        const currentAuth = JSON.stringify(getAuthHeader);
        const prevAuth = JSON.stringify(authHeaderRef.current);
        if (currentAuth !== prevAuth) {
            authHeaderRef.current = getAuthHeader;
        }
    }, [getAuthHeader]);

    const requestHeader = useMemo(
        () => ({
            "Accept-Language": i18nextLng,
            ...getAuthHeader,
        }),
        [i18nextLng, getAuthHeader]
    );

    const url = useMemo(() => {
        const params = `userId=${userID}&franchiseId=${franchiseID}&appId=${APP_ID}&typeDisplay=${typeDisplay}&uuidUser=${uuidUserRef.current}`;
        return oneShop
            ? `${APPLICATIONS_URL}/oneShop?shopId=${shopID}&${params}`
            : `${APPLICATIONS_URL}?${params}`;
    }, [oneShop, userID, franchiseID, shopID, typeDisplay]);

    useEffect(() => {
        setModify(
            getPathAuthorization(location.pathname, authorizationList).Modifie
        );
    }, [location.pathname, authorizationList]);

    const selectedRowsRef = useRef(selectedRows);
    selectedRowsRef.current = selectedRows;
    const openHistoryByType = useCallback(
        (row: SelectedRow) => {
            setModals((prev) => ({
                ...prev,
                history: false,
                printerOriginHistory: false,
                moneticHistory: false,
                cashManagementHistory: false,
            }));

            switch (row.peripheralType) {
                case "printer":
                    setUrlHistoryPrinter(
                        `${PRINTER_HISTORY_URL}?userId=${userID}&shopId=${row.shopId}&franchiseId=${franchiseID}&id=${row.peripheralId}`
                    );
                    toggleModal("printerOriginHistory", true);
                    break;

                case "monetic":
                    toggleModal("moneticHistory", true);
                    break;

                case "cash_management":
                    toggleModal("cashManagementHistory", true);
                    break;

                default:
                    toggleModal("history", true);
            }
        },
        //eslint-disable-next-line
        [toggleModal]
    );

    const arrayOfCustomColumns: customColumnProps[] = useMemo(
        () => [
            {
                indexOFColumn: oneShop ? 1 : 2,
                columnName: t("Application"),
                customJsx: (e: TableCellContext) => {
                    if (oneShop) return e.selectedRow[t("Application")];
                    return e.selectedRow[t("Shop")] !== "" ? (
                        <BadgeNumber
                            value={String(e.selectedRow.subRows?.length ?? 0)}
                        />
                    ) : (
                        e.selectedRow[t("Application")]
                    );
                },
            },
            {
                indexOFColumn: 999,
                columnName: t("Actions"),
                customJsx: (e: TableCellContext) => (
                    <CustomIcons
                        e={e}
                        setIsAddApplication={(v) =>
                            toggleModal("addApplication", v)
                        }
                        isApplicationArchived={isApplicationArchived}
                        setSelectedRows={setSelectedRows}
                        onOpenHistory={() => openHistoryByType(e.selectedRow)}
                        setIsConfirmationModalArchived={(v) =>
                            toggleModal("confirmArchived", v)
                        }
                        setIsDeviceAssociateModalOpened={(v) =>
                            toggleModal("deviceAssociate", v)
                        }
                        setIsConfirmationModalActived={(v) =>
                            toggleModal("confirmActived", v)
                        }
                        setIsDeviceDissociateModalOpened={(v) =>
                            toggleModal("deviceDissociate", v)
                        }
                        setEditedData={setEditedData}
                    />
                ),

                disableFilter: false,
            },
        ],
        //eslint-disable-next-line
        [
            oneShop,
            t,
            modals.addApplication,
            modals.history,
            isApplicationArchived,
            toggleModal,
        ]
    );

    const breadcrumbItems = useMemo(
        () => [
            {
                item: "Settings".toUpperCase(),
                link: `${process.env.REACT_APP_PORTAIL_URL}`,
            },
            {
                item: selectedAccount?.toUpperCase() || "",
                link: oneShop ? "/setting/customer-account" : "/compte",
            },
            { item: t("Application").toUpperCase(), link: "applications" },
            { item: t("POS").toUpperCase(), link: "pos" },
            {
                item: t(
                    typeDisplay === "notArchived"
                        ? "Peripheral setting"
                        : "Archived peripheral setting"
                ).toUpperCase(),
                link: "pos-peripheral-setting",
            },
        ],
        [selectedAccount, oneShop, t, typeDisplay]
    );

    const gridStyle = useMemo(
        () => ({
            display: "grid",
            minHeight: oneShop ? "650px" : "600px",
            gridTemplateColumns: modals.addApplication ? "2fr 1fr" : "auto",
            gridColumnGap: "4px",
            border: 0,
        }),
        [oneShop, modals.addApplication]
    );

    useEffect(() => {
        if (filterActive) {
            toggleModal("addApplication", false);
        }
    }, [filterActive, toggleModal]);

    useEffect(() => {
        const array = Object.values(selectedRows)
            .filter((el): el is SelectedRow => el.appId !== undefined)
            .map((el) => ({ [el.shopId ?? ""]: el.appId as string }));
        setSelectedApplicationSco(array);
    }, [selectedRows]);

    useEffect(() => {
        const { othersData } = data;
        if (othersData) {
            setNumberActive(othersData.activedApplications ?? 0);
            setNumberArchived(othersData.archivedApplications ?? 0);
        }
    }, [data]);

    useEffect(() => {
        toggleModal("addApplication", false);
        setIsEdited(false);
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        setIsDataUpdated(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18nextLng]);

    useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);

    useEffect(() => {
        if (modals.deviceAssociate) {
            toggleModal("addApplication", false);
        } else {
            // Closing the associate modal clears the table selection so the
            // multiple-action dropdown (Associate/Dissociate/Archive…) disables
            // again — the selection no longer silently persists after the modal
            // is dismissed without an action.
            setSelectedRows([]);
        }
    }, [modals.deviceAssociate, toggleModal]);

    const handleSetData = useCallback((newData: DataResponse) => {
        setData(newData);
    }, []);

    const mergedLocalApps = useMemo(() => data?.data ?? [], [data]);
    // `?? {}` makes the silent-degradation choice explicit when the BE payload
    // omits `peripheralsInUse` (old BE / contract drift) — see hook contract.
    const peripheralsInUse = useMemo(
        () => data?.othersData?.peripheralsInUse ?? {},
        [data]
    );
    useMergedAppsForMoneticStore({
        localApps: mergedLocalApps,
        peripheralsInUse,
        oneShop,
    });

    const handleSetSelectedRows: React.Dispatch<React.SetStateAction<
        SelectedRow[]
    >> = useCallback((rows) => {
        setSelectedRows(rows);
    }, []);

    const handleSetIsDataUpdated = useCallback((value: boolean | number) => {
        setIsDataUpdated(value);
    }, []);

    return (
        <>
            <PageTitle title={t("Applications")} />
            <div className="page-content__clz">
                <StyledBreadcrumb items={breadcrumbItems} />
                <MessageInfo
                    message={t(
                        "In this section, you can make the declaration and configuration of the POS screens of the store, as well as the peripherals associated with the POS application, such as printers, payment terminals, and more."
                    )}
                />
                <div style={gridStyle}>
                    <DynamicTable
                        key={i18nextLng}
                        url={url}
                        name="Item_table"
                        requestHeader={requestHeader}
                        setData={handleSetData}
                        canExpand
                        canSort
                        customSelect
                        customJsxSideFilterButton={
                            <CustomSideFilter
                                isAddApplication={modals.addApplication}
                                setIsAddApplication={(v: boolean) =>
                                    toggleModal("addApplication", v)
                                }
                                setLocalFilterActive={setLocalFilterActive}
                                setDataIsUpdated={handleSetIsDataUpdated}
                                selectedRows={selectedRows}
                                isApplicationArchived={isApplicationArchived}
                                setIsApplicationArchived={
                                    setIsApplicationArchived
                                }
                                setIsConfirmationModalActived={(v: boolean) =>
                                    toggleModal("confirmActived", v)
                                }
                                shopId={selectedRows[0]?.shopId}
                                setIsInInitializationModalOpened={(
                                    v: boolean
                                ) => toggleModal("initialization", v)}
                                setIsHistoryModalOpened={(v: boolean) =>
                                    toggleModal("history", v)
                                }
                                setIsConfirmationModalArchived={(v: boolean) =>
                                    toggleModal("confirmArchived", v)
                                }
                                isHistoryModalOpened={modals.history}
                                setSelectedRows={handleSetSelectedRows}
                                setTypeDisplay={setTypeDisplay}
                                setIsDeviceAssociateModalOpened={(v: boolean) =>
                                    toggleModal("deviceAssociate", v)
                                }
                                setIsMultiDissociateModalOpened={(v: boolean) =>
                                    toggleModal("multiDissociate", v)
                                }
                                isEditMode={Boolean(editedData?.appId)}
                            />
                        }
                        canResize
                        canSelect
                        elevationTable={1}
                        setSelectedRows={handleSetSelectedRows}
                        showGlobalFilter
                        showFilter
                        canMovedCheckboxLeftOnExpand
                        actionColumn={() => <></>}
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        filterActive={filterActive}
                        setLocalFilterActive={setLocalFilterActive}
                        setDataIsUpdated={setIsDataUpdated}
                        dataIsUpdated={isDataUpdated}
                        minHeight="500px"
                        maxHeight="500px"
                    />

                    {modals.addApplication && (
                        <AddApplication
                            isAddApplication={modals.addApplication}
                            setIsAddApplication={(v: boolean) =>
                                toggleModal("addApplication", v)
                            }
                            setDataIsUpdated={handleSetIsDataUpdated}
                            isAddNewApplicationButtonClicked={
                                isAddNewApplicationButtonClicked
                            }
                            data={data}
                            selectedRows={selectedRows}
                            editedData={editedData}
                            setEditedData={setEditedData}
                        />
                    )}

                    <ModalsContainer
                        modals={modals}
                        toggleModal={toggleModal}
                        selectedRows={selectedRows}
                        setSelectedRows={handleSetSelectedRows}
                        isDataUpdated={isDataUpdated}
                        setIsDataUpdated={handleSetIsDataUpdated}
                        isAddNewApplicationButtonClicked={
                            isAddNewApplicationButtonClicked
                        }
                        setIsAddNewApplicationButtonClicked={
                            setIsAddNewApplicationButtonClicked
                        }
                        isConfigurationPrinterClicked={
                            isConfigurationPrinterClicked
                        }
                        isConfigurationCashManagementClicked={
                            isConfigurationCashManagementClicked
                        }
                        isConfigurationMoneticClicked={
                            isConfigurationMoneticClicked
                        }
                        isActionsClicked={isActionsClicked}
                        setIsActionsClicked={setIsActionsClicked}
                        validationAction={validationAction}
                        actions={actions}
                        urlHistoryPrinter={urlHistoryPrinter}
                        oneShop={oneShop}
                        userID={String(userID)}
                        franchiseID={franchiseID}
                        shopID={shopID}
                    />
                </div>
                
            </div>
        </>
    );
}
